import {
  Icon,
  Search,
  SmallSecondaryBadge,
  Text,
  Tooltip,
} from '@joor/design-system'
import styled from 'styled-components'

import { Flex } from 'styles/components'

import { atlasClient } from 'graphql/client'

import {
  GALLERY_SEARCH_DEBOUNCE_TIME,
  UPLOAD_GALLERY_MAX_AMOUNT,
} from 'features/Linesheets/Modals/ManageImagesModal/manageImagesModal.constants'
import {
  useBulkRemoveUploads,
  useGetDropzoneState,
} from 'features/Linesheets/Modals/ManageImagesModal/manageImagesModal.hooks'
import { GalleryIds } from 'features/Linesheets/Modals/ManageImagesModal/manageImagesModal.ids'
import messages from 'features/Linesheets/Modals/ManageImagesModal/manageImagesModal.messages'
import useStore from 'features/Linesheets/Modals/ManageImagesModal/store/useManageImagesStore'

const ActionBarContainer = styled.section`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  height: 40px;

  & > div:nth-child(2) {
    flex: 1;

    & > input[disabled] {
      cursor: not-allowed;
    }
  }
`

const IconContianer = styled.div`
  display: flex;
  gap: 10px;
  padding: 5px 10px;
  background-color: var(--color-secondary-100);
  border-radius: 10px;
  min-width: 1px;
`

const DeselectContainer = styled.span`
  position: relative;
`

const SelectionCounter = styled(SmallSecondaryBadge)`
  position: absolute;
  top: -5px;
  right: -5px;
  background-color: var(--color-error-500);
  cursor: pointer;
`

const StyledIcon = styled(Icon)`
  cursor: pointer;

  &.primary-500 svg {
    --main-color: var(--color-primary-500);

    &:hover {
      --main-color: var(--color-secondary-600);
    }
  }

  &.disabled {
    cursor: not-allowed;

    svg {
      --main-color: var(--color-primary-300);
    }
  }
`

type ActionBarProps = {
  isLoading: boolean
}

const ActionBar = ({ isLoading }: ActionBarProps): JSX.Element => {
  const currentPage = useStore((state) => state.currentPage)
  const setCurrentPage = useStore((state) => state.setCurrentPage)
  const lastPage = useStore((state) => state.lastPage)
  const search = useStore((state) => state.search)
  const setSearch = useStore((state) => state.setSearch)
  const setIsRemovingImages = useStore((state) => state.setIsRemovingImages)
  const selectedImages = useStore((state) => state.selectedImages)
  const setSelectedImages = useStore((state) => state.setSelectedImages)
  const isRemovingImages = useStore((state) => state.isRemovingImages)
  const isSavingImages = useStore((state) => state.isSavingImages)
  const isUploadingImages = useStore((state) => state.isUploadingImages)
  const { bulkRemoveUploads } = useBulkRemoveUploads()

  const areActionsDisabled =
    isLoading || isRemovingImages || isSavingImages || isUploadingImages

  const { getInputProps, getRootProps } = useGetDropzoneState(
    undefined,
    UPLOAD_GALLERY_MAX_AMOUNT,
    areActionsDisabled,
  )

  const handleDeselectAll = () => {
    if (areActionsDisabled) return
    setSelectedImages({ selectedImages: new Map() })
  }

  const handleRemoveImages = async () => {
    if (areActionsDisabled) return
    setIsRemovingImages({ isRemovingImages: true })
    const { error } = await bulkRemoveUploads()
    if (!error) {
      setSelectedImages({ selectedImages: new Map() })
      setCurrentPage({ currentPage: 1 })
      atlasClient.cache.evict({ fieldName: 'listUnmatchedUploads' })
    }
    setIsRemovingImages({ isRemovingImages: false })
  }

  return (
    <ActionBarContainer>
      <IconContianer>
        <DeselectContainer
          data-testid={GalleryIds.DeselectAllButton}
          onClick={handleDeselectAll}
        >
          <StyledIcon
            {...(areActionsDisabled || !selectedImages.size
              ? { className: 'disabled', variant: 'DISABLED' }
              : { className: 'primary-500', variant: 'DEFAULT' })}
            iconName="minus-circle-outline"
          />
          {!!selectedImages.size && (
            <SelectionCounter value={selectedImages.size} />
          )}
        </DeselectContainer>
        <Tooltip
          content={
            areActionsDisabled
              ? ''
              : messages.uploadUpToNImages.replace(
                  ':limit',
                  UPLOAD_GALLERY_MAX_AMOUNT.toString(),
                )
          }
        >
          <span {...getRootProps()} data-testid={GalleryIds.UploadButton}>
            <StyledIcon
              {...(areActionsDisabled
                ? { className: 'disabled', variant: 'DISABLED' }
                : { className: 'primary-500', variant: 'DEFAULT' })}
              iconName="upload"
            />
            <input {...getInputProps()} />
          </span>
        </Tooltip>
        <StyledIcon
          data-testid={GalleryIds.RemoveButton}
          {...(areActionsDisabled || !selectedImages.size
            ? { className: 'disabled', variant: 'DISABLED' }
            : { className: 'primary-500', variant: 'DEFAULT' })}
          iconName="trash"
          onClick={handleRemoveImages}
        />
      </IconContianer>

      <Search
        id={GalleryIds.Search}
        data-testid={GalleryIds.Search}
        placeholder={messages.searchAllImages}
        disabled={areActionsDisabled}
        incrementalSearch
        debounceTime={GALLERY_SEARCH_DEBOUNCE_TIME}
        value={search}
        onSearch={({ searchText }) => {
          setSearch({ search: searchText })
          setCurrentPage({ currentPage: 1 })
        }}
      />

      <Flex alignItems="center">
        <StyledIcon
          data-testid={GalleryIds.PreviousButton}
          {...(areActionsDisabled || currentPage <= 1
            ? { className: 'disabled', variant: 'DISABLED' }
            : { variant: 'DEFAULT' })}
          iconName="chevron-left"
          onClick={() => setCurrentPage({ currentPage: currentPage - 1 })}
        />
        <Text
          fontWeight="medium"
          fontSize="small"
          letterSpacing="tighter"
          color={areActionsDisabled ? 'primary-300' : 'primary-900'}
        >{`${currentPage} ${messages.of} ${lastPage}`}</Text>
        <StyledIcon
          data-testid={GalleryIds.NextButton}
          {...(areActionsDisabled || currentPage >= (lastPage as number)
            ? { className: 'disabled', variant: 'DISABLED' }
            : { variant: 'DEFAULT' })}
          iconName="chevron-right"
          onClick={() => setCurrentPage({ currentPage: currentPage + 1 })}
        />
      </Flex>
    </ActionBarContainer>
  )
}

export default ActionBar
